import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { batchedSubscribe } from 'redux-batched-subscribe';
import { combineEpics, createEpicMiddleware } from 'redux-observable';

import appReducer from './app';
import categoriesReducer from './categories';
import configReducer from './config';
import dialogReducer from './dialog';
import createThrottle from './enhancers/batch';
import rafUpdateBatcher from './enhancers/rafUpdateBatcher';
import analyticsEpic from './epics/analytics';
import configEpic from './epics/config';
import { historyEpic, dayHistoryEpic } from './epics/history';
import navigationEpic from './epics/navigation';
import notificationsEpic from './epics/notifications';
import postMessageEpic from './epics/postMessage';
import socketEpic from './epics/socket';
import tablesEpic from './epics/tables';
import userEpic from './epics/user';
import externalGameTypesReducer from './externalGameTypes';
import historyReducer from './history';
import localeReducer from './locale';
import notificationsReducer from './notifications';
import tablesReducer, { tables } from './tables';
import uiReducer from './ui';
import userReducer from './user';
import widgetReducer from './widget';

export const rootReducer = combineReducers({
  ui: uiReducer,
  app: appReducer,
  user: userReducer,
  config: configReducer,
  locale: localeReducer,
  categories: categoriesReducer,
  tables: tablesReducer,
  history: historyReducer,
  widget: widgetReducer,
  notifications: notificationsReducer,
  dialog: dialogReducer,
  externalGameTypes: externalGameTypesReducer,
});

const rootEpic = combineEpics(
  userEpic,
  configEpic,
  socketEpic,
  tablesEpic,
  navigationEpic,
  postMessageEpic,
  historyEpic,
  dayHistoryEpic,
  analyticsEpic,
  notificationsEpic,
);

const epicMiddleware = createEpicMiddleware();

export const throttleConfig = {
  [tables.updateTable]: {
    defaultValue: [],
    throttleTime: 500,
    shouldMerge: true,
  },
};

const throttleMiddleware = createThrottle(throttleConfig);

export default function configureAppStore(preloadedState) {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => []
      .concat(throttleMiddleware)
      .concat(getDefaultMiddleware({ serializableCheck: false }))
      .concat(epicMiddleware),
    preloadedState,
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat([ batchedSubscribe(rafUpdateBatcher) ]),
  });

  if (import.meta.env.MODE === 'development') {
    window.__REDUX_STORE__ = store;
  }

  epicMiddleware.run(rootEpic);

  return store;
}
